/**
 * Audit Page
 *
 * View and search audit logs for compliance
 */

import { useState, Fragment } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Shield,
  Filter,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye,
  Clock,
} from 'lucide-react'

import {
  useAuditLogs,
  useSecurityAlerts,
  formatAuditTimestamp,
  AUDIT_ACTION_LABELS,
  AUDIT_RESOURCE_LABELS,
  getAuditStatusVariant,
  getActionColor,
} from '@/application/audit'
import type { AuditLogFilters, AuditAction, AuditStatus, AuditResourceType } from '@/domain/audit'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Badge } from '@/presentation/components/ui/Badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'

// =============================================================================
// Component
// =============================================================================

export function AuditPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [showFilters, setShowFilters] = useState(false)
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null)

  // Filters state
  const [filters, setFilters] = useState<AuditLogFilters>({
    action: (searchParams.get('action') as AuditAction) || undefined,
    resourceType: (searchParams.get('resourceType') as AuditResourceType) || undefined,
    status: (searchParams.get('status') as AuditStatus) || undefined,
    dateFrom: searchParams.get('dateFrom') || undefined,
    dateTo: searchParams.get('dateTo') || undefined,
    limit: 50,
    offset: parseInt(searchParams.get('offset') || '0', 10),
  })

  // Queries
  const { data, isLoading, isError, error } = useAuditLogs(filters)
  const { data: alerts } = useSecurityAlerts({ limit: 10 })

  // Pagination
  const currentPage = Math.floor((filters.offset || 0) / (filters.limit || 50)) + 1
  const totalPages = data ? Math.ceil(data.pagination.total / (filters.limit || 50)) : 0

  const goToPage = (page: number) => {
    const offset = (page - 1) * (filters.limit || 50)
    setFilters((prev) => ({ ...prev, offset }))
    setSearchParams((params) => {
      params.set('offset', offset.toString())
      return params
    })
  }

  // Update filters
  const updateFilters = (newFilters: Partial<AuditLogFilters>) => {
    const updated = { ...filters, ...newFilters, offset: 0 }
    setFilters(updated)

    const params = new URLSearchParams()
    if (updated.action) params.set('action', updated.action)
    if (updated.resourceType) params.set('resourceType', updated.resourceType)
    if (updated.status) params.set('status', updated.status)
    if (updated.dateFrom) params.set('dateFrom', updated.dateFrom)
    if (updated.dateTo) params.set('dateTo', updated.dateTo)
    setSearchParams(params)
  }

  const clearFilters = () => {
    setFilters({ limit: 50, offset: 0 })
    setSearchParams({})
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--foreground)]">Auditoría</h1>
          <p className="text-[var(--foreground-muted)]">
            Registro de actividad del sistema - Cumplimiento normativo
          </p>
        </div>
      </div>

      {/* Security Alerts Banner */}
      {alerts && alerts.alerts.length > 0 && (
        <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <div>
                <p className="font-medium text-red-800 dark:text-red-200">
                  {alerts.total} alertas de seguridad recientes
                </p>
                <p className="text-sm text-red-600 dark:text-red-400">
                  Intentos de acceso fallidos o denegados en las últimas 24 horas
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filtros
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Ocultar' : 'Mostrar'}
            </Button>
          </div>
        </CardHeader>
        {showFilters && (
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {/* Action */}
              <div>
                <label className="text-sm font-medium text-[var(--foreground-muted)] mb-1 block">
                  Acción
                </label>
                <select
                  className="w-full h-10 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
                  value={filters.action || ''}
                  onChange={(e) =>
                    updateFilters({ action: (e.target.value as AuditAction) || undefined })
                  }
                >
                  <option value="">Todas</option>
                  {Object.entries(AUDIT_ACTION_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Resource Type */}
              <div>
                <label className="text-sm font-medium text-[var(--foreground-muted)] mb-1 block">
                  Recurso
                </label>
                <select
                  className="w-full h-10 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
                  value={filters.resourceType || ''}
                  onChange={(e) =>
                    updateFilters({
                      resourceType: (e.target.value as AuditResourceType) || undefined,
                    })
                  }
                >
                  <option value="">Todos</option>
                  {Object.entries(AUDIT_RESOURCE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status */}
              <div>
                <label className="text-sm font-medium text-[var(--foreground-muted)] mb-1 block">
                  Estado
                </label>
                <select
                  className="w-full h-10 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
                  value={filters.status || ''}
                  onChange={(e) =>
                    updateFilters({ status: (e.target.value as AuditStatus) || undefined })
                  }
                >
                  <option value="">Todos</option>
                  <option value="SUCCESS">Exitoso</option>
                  <option value="FAILURE">Fallido</option>
                  <option value="DENIED">Denegado</option>
                </select>
              </div>

              {/* Date From */}
              <div>
                <label className="text-sm font-medium text-[var(--foreground-muted)] mb-1 block">
                  Desde
                </label>
                <Input
                  type="date"
                  value={filters.dateFrom || ''}
                  onChange={(e) => updateFilters({ dateFrom: e.target.value || undefined })}
                />
              </div>

              {/* Date To */}
              <div>
                <label className="text-sm font-medium text-[var(--foreground-muted)] mb-1 block">
                  Hasta
                </label>
                <Input
                  type="date"
                  value={filters.dateTo || ''}
                  onChange={(e) => updateFilters({ dateTo: e.target.value || undefined })}
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button variant="outline" onClick={clearFilters}>
                Limpiar filtros
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Results */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
              <p className="text-red-500">{error?.message || 'Error al cargar logs'}</p>
            </div>
          ) : !data?.logs.length ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Shield className="h-12 w-12 text-[var(--foreground-muted)] mb-4" />
              <p className="text-[var(--foreground-muted)]">No se encontraron registros</p>
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[var(--border)] bg-[var(--background-subtle)]">
                      <th className="text-left p-4 font-medium text-[var(--foreground-muted)]">
                        Fecha/Hora
                      </th>
                      <th className="text-left p-4 font-medium text-[var(--foreground-muted)]">
                        Usuario
                      </th>
                      <th className="text-left p-4 font-medium text-[var(--foreground-muted)]">
                        Acción
                      </th>
                      <th className="text-left p-4 font-medium text-[var(--foreground-muted)]">
                        Recurso
                      </th>
                      <th className="text-left p-4 font-medium text-[var(--foreground-muted)]">
                        Estado
                      </th>
                      <th className="text-left p-4 font-medium text-[var(--foreground-muted)]">
                        IP
                      </th>
                      <th className="text-right p-4 font-medium text-[var(--foreground-muted)]">
                        Detalles
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.logs.map((log) => (
                      <Fragment key={log.id}>
                        <tr
                          className={`border-b border-[var(--border)] hover:bg-[var(--background-subtle)] transition-colors cursor-pointer ${
                            log.status === 'DENIED' || log.status === 'FAILURE'
                              ? 'bg-red-50 dark:bg-red-950/40'
                              : ''
                          }`}
                          onClick={() =>
                            setExpandedLogId(expandedLogId === log.id ? null : log.id)
                          }
                        >
                          <td className="p-4 text-sm">
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-[var(--foreground-muted)]" />
                              {formatAuditTimestamp(log.timestamp)}
                            </div>
                          </td>
                          <td className="p-4 text-sm">
                            {log.user.email || (
                              <span className="text-[var(--foreground-muted)]">Anónimo</span>
                            )}
                            {log.user.role && (
                              <p className="text-xs text-[var(--foreground-muted)]">
                                {log.user.role}
                              </p>
                            )}
                          </td>
                          <td className="p-4">
                            <span className={`text-sm font-medium ${getActionColor(log.action)}`}>
                              {AUDIT_ACTION_LABELS[log.action]}
                            </span>
                          </td>
                          <td className="p-4 text-sm">
                            <span>{AUDIT_RESOURCE_LABELS[log.resource.type]}</span>
                            {log.resource.name && (
                              <p className="text-xs text-[var(--foreground-muted)]">
                                {log.resource.name}
                              </p>
                            )}
                          </td>
                          <td className="p-4">
                            <Badge variant={getAuditStatusVariant(log.status)}>
                              {log.status === 'SUCCESS' && (
                                <CheckCircle className="h-3 w-3 mr-1" />
                              )}
                              {log.status === 'FAILURE' && <XCircle className="h-3 w-3 mr-1" />}
                              {log.status === 'DENIED' && (
                                <AlertTriangle className="h-3 w-3 mr-1" />
                              )}
                              {log.status}
                            </Badge>
                          </td>
                          <td className="p-4 text-sm text-[var(--foreground-muted)]">
                            {log.request.ip || '-'}
                          </td>
                          <td className="p-4 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation()
                                setExpandedLogId(expandedLogId === log.id ? null : log.id)
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                        {expandedLogId === log.id && (
                          <tr>
                            <td colSpan={7} className="p-4 bg-[var(--background-subtle)]">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <p className="text-xs text-[var(--foreground-muted)]">
                                    ID de Registro
                                  </p>
                                  <p className="font-mono text-xs">{log.id}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-[var(--foreground-muted)]">
                                    ID de Recurso
                                  </p>
                                  <p className="font-mono text-xs">{log.resource.id || '-'}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-[var(--foreground-muted)]">Método</p>
                                  <p>{log.request.method || '-'}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-[var(--foreground-muted)]">Ruta</p>
                                  <p className="font-mono text-xs">{log.request.path || '-'}</p>
                                </div>
                              </div>
                              {log.errorMessage && (
                                <div className="mt-4 p-3 bg-red-100 dark:bg-red-900/40 rounded text-red-700 dark:text-red-300 text-sm">
                                  <strong>Error:</strong> {log.errorMessage}
                                </div>
                              )}
                              {log.details && (
                                <div className="mt-4">
                                  <p className="text-xs text-[var(--foreground-muted)] mb-1">
                                    Detalles
                                  </p>
                                  <pre className="text-xs bg-[var(--background)] p-2 rounded overflow-x-auto">
                                    {JSON.stringify(log.details, null, 2)}
                                  </pre>
                                </div>
                              )}
                              {log.changes && (
                                <div className="mt-4 grid grid-cols-2 gap-4">
                                  {log.changes.before && (
                                    <div>
                                      <p className="text-xs text-[var(--foreground-muted)] mb-1">
                                        Antes
                                      </p>
                                      <pre className="text-xs bg-red-50 dark:bg-red-950/40 p-2 rounded overflow-x-auto">
                                        {JSON.stringify(log.changes.before, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                  {log.changes.after && (
                                    <div>
                                      <p className="text-xs text-[var(--foreground-muted)] mb-1">
                                        Después
                                      </p>
                                      <pre className="text-xs bg-green-50 dark:bg-green-950/40 p-2 rounded overflow-x-auto">
                                        {JSON.stringify(log.changes.after, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-[var(--border)]">
                  <p className="text-sm text-[var(--foreground-muted)]">
                    Mostrando {(filters.offset || 0) + 1} -{' '}
                    {Math.min(
                      (filters.offset || 0) + (filters.limit || 50),
                      data.pagination.total
                    )}{' '}
                    de {data.pagination.total} registros
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-[var(--foreground-muted)]">
                      Página {currentPage} de {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
